<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'w-full sm:max-w-lg' }">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard :ui="{ rounded: 'rounded-lg', body: { base: 'overflow-visible' } }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Edit Profile</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                </template>
                <div class="grid grid-cols-6 gap-x-4 gap-y-6">
                    <UFormGroup class="col-span-3" name="first_name" label="First Name" required>
                        <UInput v-model="state.first_name" type="text" size="lg" />
                    </UFormGroup>
                    <UFormGroup class="col-span-3" name="last_name" label="Last Name" required>
                        <UInput v-model="state.last_name" type="text" size="lg" />
                    </UFormGroup>
                    <UFormGroup class="col-span-6" name="middle_name" label="Father's Name" required>
                        <UInput v-model="state.middle_name" type="text" size="lg" />
                    </UFormGroup>
                    <UFormGroup class="col-span-2" name="birth_date" label="Date of Birth" required>
                        <UInput v-model="state.birth_date" type="date" size="lg" />
                    </UFormGroup>
                    <UFormGroup class="col-span-2" name="gender" label="Gender" required>
                        <USelectMenu v-model="state.gender" :options="genders" value-attribute="value" size="lg" trailing />
                    </UFormGroup>
                    <UFormGroup class="col-span-2" name="blood_type" label="Blood Type">
                        <USelectMenu v-model="state.blood_type" :options="bloodTypes" value-attribute="value" size="lg" trailing />
                    </UFormGroup>
                    <UFormGroup class="col-span-3" name="emergency_contact" label="Emergency Contact">
                        <UInput v-model="state.emergency_contact" type="text" size="lg" />
                    </UFormGroup>
                    <UFormGroup class="col-span-3" name="emergency_phone" label="Emergency Phone">
                        <InputPhone v-model="state.emergency_phone" />
                    </UFormGroup>
                    <UFormGroup class="col-span-6" name="alternate_phone" label="Alternate Phone">
                        <InputPhone v-model="state.alternate_phone" />
                    </UFormGroup>
                    <UFormGroup class="col-span-6" name="policy_holder" label="Policy Holder">
                        <UInputMenu
                            v-model="state.policy_holder"
                            :search="searchPolicyHolders"
                            nullable
                            size="lg"
                            placeholder="Search..."
                        >
                            <template #option-empty="{ query }">
                                Add <q>{{ query }}</q>
                            </template>
                        </UInputMenu>
                    </UFormGroup>
                    <UFormGroup class="col-span-6" name="tags" label="Tags">
                        <UInput
                            v-model="tagsInputData"
                            placeholder="Search tags..."
                            size="lg"
                            @input="handleTagsInput"
                            @keydown.enter.prevent="addTag"
                            @keydown.backspace="handleTagsBackspace"
                        />

                        <UPopover :open="!!tagsInputData" :ui="{ trigger: 'invisible !h-0 w-full', width: 'w-[464px]' }">
                            <template #panel="{ close }">
                                <div class="w-full divide-y divide-gray-100">
                                    <UButton
                                        v-for="suggestion in tagsSuggestions"
                                        :key="suggestion"
                                        variant="ghost"
                                        color="gray"
                                        class="w-full text-left rounded-none"
                                        size="lg"
                                        @click="selectTagsSuggestion(suggestion); close()"
                                    >
                                        {{ suggestion }}
                                    </UButton>
                                    <UButton
                                        v-if="isNewTag"
                                        variant="ghost"
                                        color="gray"
                                        class="w-full text-left rounded-none"
                                        size="lg"
                                        @click="selectTagsSuggestion(tagsInputData); close()"
                                    >
                                        <UIcon name="i-ph-plus-bold" class="size-4 text-gray-400" />
                                        <span>Add "{{ tagsInputData }}"</span>
                                    </UButton> 
                                </div>  
                            </template>
                        </UPopover>

                        <div class="-mt-3">
                            <div v-if="!state.tags.length" class="px-2">
                                <p class="text-xs italic text-gray-400">No assigned tags.</p>
                            </div>
                            <div v-else class="flex flex-wrap gap-2">
                                <UBadge v-for="tag in state.tags" :key="tag" color="gray" size="xs">
                                    <div class="flex items-center space-x-1 ps-1">
                                        <span class="text-sm">{{ tag }}</span>
                                        <UButton
                                            @click="removeTag(tag)"
                                            icon="i-ph-x-bold"
                                            size="2xs"
                                            color="gray"
                                            variant="ghost"
                                        />
                                    </div>
                                </UBadge>
                            </div>
                        </div>
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                        <UButton :loading="loading" type="submit" size="lg" color="black"> Save </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </UModal>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { normalizeStoredPhone, phoneSchema } from '~/utils/phone'
import type { FormSubmitEvent } from '#ui/types'
import { BloodType, Gender, type Customer } from '~/types/customer'
import { useDebounce } from '@vueuse/core'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    customer?: Customer
}>()

const client = useSupabaseClient()
const toast = useToast()

const form = ref()
const state = ref<z.infer<typeof schema>>()
const loading = ref<boolean>(false)
const tagsInputData = ref<string>('')
const tagsSuggestions = ref<string[]>([])
const debouncedTagsInput = useDebounce(tagsInputData, 300)
const isNewTag = computed(() => tagsInputData.value && !state.value?.tags.includes(tagsInputData.value) && !tagsSuggestions.value.includes(tagsInputData.value))

const schema = z.object({
    first_name: z.string(),
    middle_name: z.string(),
    last_name: z.string(),
    birth_date: z.string().optional(),
    gender: z.nativeEnum(Gender).optional(),
    blood_type: z.nativeEnum(BloodType).optional(),
    emergency_contact: z.string().optional(),
    emergency_phone: phoneSchema(),
    alternate_phone: phoneSchema(),
    policy_holder: z.string().optional().nullable(),
    tags: z.array(z.string()),
})

const genders = [
    { label: 'Male', value: Gender.male },
    { label: 'Female', value: Gender.female }
]

const bloodTypes = Object.values(BloodType).map((value) => {
    return { label: value, value: value }
})

watch(() => props.open, (open) => {
    if (open) {
        state.value = {
            first_name: props.customer?.first_name || '',
            middle_name: props.customer?.middle_name || '',
            last_name: props.customer?.last_name || '',
            birth_date: props.customer?.birth_date,
            gender: props.customer?.gender,
            blood_type: props.customer?.blood_type,
            emergency_contact: props.customer?.metadata?.emergency_contact,
            emergency_phone: normalizeStoredPhone(props.customer?.metadata?.emergency_phone),
            alternate_phone: normalizeStoredPhone(props.customer?.metadata?.alternate_phone),
            policy_holder: props.customer?.policy_holder,
            tags: props.customer?.tags || [],
        }
    }
})

watch(debouncedTagsInput, async (newValue) => {
    if (newValue.length > 0) {
        try {
            const { data, error } = await client
                .rpc('get_customer_tag_suggestions', { partial_tag: newValue } as never)
                .returns<{ tag: string }[]>()

            if (error) throw error

            tagsSuggestions.value = data.map((t) => t.tag)
        } catch (error: any) {
            tagsSuggestions.value = []
        }
    } else {
        tagsSuggestions.value = []
    }
})

function handleTagsInput() {
    if (tagsInputData.value?.endsWith(' ')) {
        addTag()
    }
}

function addTag() {
    const newTag = tagsInputData.value.trim()
    if (newTag && state.value && !state.value.tags.includes(newTag)) {
        state.value.tags.push(newTag)
        tagsInputData.value = ''
        tagsSuggestions.value = []
    }
}

function removeTag(tag: string) {
    if (state.value) {
        state.value.tags = state.value.tags.filter((t) => t !== tag)
    }
}

function selectTagsSuggestion(suggestion: string) {
    if (state.value && !state.value.tags.includes(suggestion)) {
        state.value.tags.push(suggestion)
        tagsInputData.value = ''
        tagsSuggestions.value = []
    }
}

function handleTagsBackspace() {
    if (tagsInputData.value === '' && state.value?.tags?.length) {
        removeTag(state.value.tags[state.value.tags.length - 1])
    }
}

async function searchPolicyHolders(q: string) {
    const { data, error } = await client
        .rpc('get_policy_holder_suggestions', { partial_name: q } as never)
        .returns<{ policy_holder: string }[]>()

    if (error || !data.length) {
        if (q && state.value) {
            state.value.policy_holder = q
        }
        return []
    }

    return data.map((t) => t.policy_holder)
}

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    if (props.customer?.id) {
        loading.value = true

        const data = {
            first_name: event.data.first_name,
            middle_name: event.data.middle_name,
            last_name: event.data.last_name,
            birth_date: event.data.birth_date,
            gender: event.data.gender,
            blood_type: event.data.blood_type,
            tags: event.data.tags,
            policy_holder: event.data.policy_holder?.toUpperCase() ?? null,
            metadata: {
                ...props.customer.metadata,
                emergency_contact: event.data.emergency_contact,
                emergency_phone: event.data.emergency_phone || undefined,
                alternate_phone: event.data.alternate_phone || undefined,
            }
        } as never

        const { error } = await client.from('customers')
            .update(data)
            .eq('id', props.customer.id)

        loading.value = false

        if (error) {
            toast.add({
                title: 'An error has occurred, try again later.',
                color: 'red',
                icon: 'i-ph-x-circle-duotone'
            })
            
            dismiss()
        } else {
            toast.add({
                title: 'Customer profile updated!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            dismiss(true)
        }
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload')
    }
}
</script>
